import { ItemTypeConsts } from 'client/util/Constants';
import { legacyEvents } from './LegacyEvents';

const genericEvents = {
  // Generic Events
  asideViewed: 'Aside Viewed',
  buttonClicked: 'Button Clicked',
  checkboxClicked: 'Checkbox Clicked',
  dropDownItemSelected: 'Dropdown Item Selected',
  iconClicked: 'Icon Clicked',
  inputEntered: 'Input Entered',
  linkClicked: 'Link Clicked',
  toggleClicked: 'Toggle Clicked',
  modalViewed: 'Modal Viewed',
  nudgeViewed: 'Nudge Viewed',
  pageViewed: 'Page Viewed',
  tabClicked: 'Tab Clicked',

  // Home Page card Events - What is a widget?
  widgetClicked: 'Widget Clicked',
};

/**
 * DO NOT ADD TO THIS LIST OF EVENTS
 * We may need to add to genericEvents object above
 * If we need to add to genericEvents we want to follow the JC Segment Framework
 * A list of event names that are tracked throughout the UI via Segment.
 * If the event name is an exact match with an Optimizely event name, the event will
 * propagate to Optimizely.
 */
export const events = {
  ...genericEvents,
  ...legacyEvents,
};

export const eventProperties = {
  boolean: 'Boolean',
  chipState: 'Chip State',
  description: 'Description',
  displayLogic: 'Display Logic',
  fromUrl: 'From URL',
  iconName: 'Icon Name',
  location: 'Location',
  multiSelectSubmitted: 'Multi Select Submitted',
  section: 'Section',
  singleSelectSubmitted: 'Single Select Submitted',
  submittedText: 'Submitted Text',
  tab: 'Tab',
  text: 'Text',
  toUrl: 'To URL',
};

export const eventLocations = {
  accountBillingFormModal: 'Account Billing Form Modal',
  accountPage: 'Account Page',
  activeDirectoryPage: 'Active Directory Page',
  authIdpAnnouncementPopover: 'Auth IDP Announcement Popover',
  billingPage: 'Billing Page',
  cloudDirectoriesPage: 'Cloud Directories Page',
  commandsPage: 'Commands Page',
  conditionalListsPage: 'Conditional Lists Page',
  conditionalPoliciesPage: 'Conditional Policies Page',
  conditionalPoliciesAside: 'Conditional Policies Aside',
  conditionalPoliciesTrialModal: 'Conditional Policies Trial Modal',
  deviceGroupsPage: 'Device Groups Page',
  devicesPage: 'Devices Page',
  discoverPage: 'Discover Page',
  homePage: 'Home Page',
  hrDirectoriesPage: 'HR Directories Page',
  immersiveOnboarding: 'Immersive Onboarding',
  implementationChecklistLoadingModal: 'Implementation Checklist Loading Modal',
  implementationChecklistSidebar: 'Implementation Checklist Sidebar',
  implementationGuidesPage: 'Implementation Guides Page',
  ldapPage: 'LDAP Page',
  leftHandNavigation: 'Left Hand Navigation',
  macFileVaultTwoPolicyAside: 'Mac File Vault 2 Policy Aside',
  mdmPage: 'MDM Page',
  mfaConfigurationsPage: 'MFA Configurations Page',
  nudgeAlert: 'Nudge Alert',
  onboardingModal: 'Onboarding Modal',
  patchManagementAnnouncementPopover: 'Patch Management Announcement Popover',
  patchManagementTrialModal: 'Patch Management Trial Modal',
  paymentInfoForNoCCModal: 'Payment Information Modal',
  paymentIntroModal: 'Payment Intro Modal',
  paymentSetupModal: 'Payment Setup Modal',
  paymentThankYouModal: 'Payment Thank You Modal',
  policyGroupsPage: 'Policy Groups Page',
  policyManagementPage: 'Policy Management Page',
  pricingCalculatorModal: 'Pricing Calculator Modal',
  pricingPage: 'Pricing Page',
  radiusPage: 'Radius Page',
  resourcesSidebar: 'Resources Sidebar',
  settingsPage: 'Settings Page',
  signup: 'Sign Up',
  simulationModal: 'Simulation Modal',
  softwareManagementPage: 'Software Management Page',
  ssoPage: 'SSO Page',
  supportPage: 'Support Page',
  supportSidebar: 'Support Sidebar',
  upgradeNudge: 'Upgrade Nudge',
  upgradePage: 'Upgrade Page',
  upperNavigation: 'Upper Navigation',
  userGroupsPage: 'User Groups Page',
  usersPage: 'Users Page',
  useCaseOnboarding: 'Use-Case Onboarding',
  valueReport: 'Value Report Page',
};

export const itemTypePages = {
  [ItemTypeConsts.activeDirectory]: eventLocations.activeDirectoryPage,
  [ItemTypeConsts.application]: eventLocations.ssoPage,
  [ItemTypeConsts.authNPolicies]: eventLocations.conditionalPoliciesPage,
  [ItemTypeConsts.cloudDirectory]: eventLocations.cloudDirectoriesPage,
  [ItemTypeConsts.command]: eventLocations.commandsPage,
  [ItemTypeConsts.configuration]: eventLocations.policyManagementPage,
  [ItemTypeConsts.configurationGroup]: eventLocations.policyGroupsPage,
  [ItemTypeConsts.device]: eventLocations.devicesPage,
  [ItemTypeConsts.deviceGroup]: eventLocations.deviceGroupsPage,
  [ItemTypeConsts.gSuite]: eventLocations.cloudDirectoriesPage,
  [ItemTypeConsts.hrDirectory]: eventLocations.hrDirectoriesPage,
  [ItemTypeConsts.ipList]: eventLocations.conditionalListsPage,
  [ItemTypeConsts.ldap]: eventLocations.ldapPage,
  [ItemTypeConsts.office365]: eventLocations.cloudDirectoriesPage,
  [ItemTypeConsts.radiusServer]: eventLocations.radiusPage,
  [ItemTypeConsts.softwareApps]: eventLocations.softwareManagementPage,
  [ItemTypeConsts.sso]: eventLocations.ssoPage,
  [ItemTypeConsts.systemUser]: eventLocations.usersPage,
  [ItemTypeConsts.userGroup]: eventLocations.userGroupsPage,
  [ItemTypeConsts.workday]: eventLocations.hrDirectoriesPage,
};

export const paymentModalNames = {
  paymentContactSalesModal: 'Payment Contact Sales Modal',
  paymentIntroModal: 'Payment Intro Modal',
  paymentSetup: 'Payment Setup Modal',
  paymentOutroModal: 'Payment Outro Modal',
  paymentInfoForNoCCModal: 'Payment Information Modal',
};

export const paymentSources = {
  buyNowButton: 'Buy Now Button',
  manualUserEntry: 'Manual User Entry',
  modalHeaderButton: 'Modal Header Button',
  userImport: 'User Import',
  userMenuDropdown: 'User Menu Dropdown',
  pastDueAccountBanner: 'Past Due Account Banner',
};

export const onboardingTasks = {
  createAdminAccount: {
    title: 'Create an admin account',
  },
  discoverResources: {
    title: 'Add an IT resource',
  },
  systemCreated: {
    title: 'Set up a system',
  },
  userBoundToUserGroup: {
    title: 'Add user(s) to user group',
  },
  userBoundToDevice: {
    title: 'Connect your user to a device',
  },
  userCreated: {
    title: 'Add a user',
  },
  userGroupCreated: {
    title: 'Create a user group',
  },
};

export const defaultOnboardingFeatures = {
  createAdminAccount: onboardingTasks.createAdminAccount.title,
};

export const onboardingFeatures = {
  appleMdm: 'appleMdm',
  compliance: 'compliance',
  deviceManagement: 'deviceManagement',
  ldap: 'ldap',
  mfa: 'mfa',
  passwordManagement: 'passwordManagement',
  radius: 'radius',
  remoteAssist: 'remoteAssist',
  serverManagement: 'serverManagement',
  ssoAuth: 'ssoAuth',
  userManagement: 'userManagement',
};

// START NE-2079
// Experiment Constants for https://jumpcloud.atlassian.net/browse/NE-2079
// A mapping of UTM term for people who search for jumpcloud with another term
export const landingPageReferralUrls = {
  cloudDirectory: 'https://jumpcloud.com/lp/cloud-directory',
  cloudDirectoryFavaBean: 'https://jumpcloud.com/lp/cloud-directory-fava-bean',
  crossOsDeviceManagement: 'https://jumpcloud.com/lp/cross-os-device-management',
  crossOsDeviceManagementFavaBean: 'https://jumpcloud.com/lp/cross-os-device-management-fava-bean',
};

export const termToFeatureMap = {
  azure: onboardingFeatures.userManagement, // azure searches lean towards User Management and Auth
  'directory-as-a-service': onboardingFeatures.userManagement,
  google: onboardingFeatures.userManagement, // assumption is they meant gsuite/google integration
  gsuite: onboardingFeatures.userManagement,
  ldap: onboardingFeatures.ldap,
  mdm: onboardingFeatures.appleMdm,
  okta: onboardingFeatures.ssoAuth,
  radius: onboardingFeatures.radius,
  'remote-assist': onboardingFeatures.remoteAssist,
  saml: onboardingFeatures.ssoAuth,
  sso: onboardingFeatures.ssoAuth,
};
// END NE-2079

export const onboardingFeatureDisplayNames = {
  [onboardingFeatures.appleMdm]: 'Apple MDM',
  [onboardingFeatures.compliance]: 'Event Logging & Reporting',
  [onboardingFeatures.deviceManagement]: 'Device Management & Authentication',
  [onboardingFeatures.ldap]: 'Cloud LDAP',
  [onboardingFeatures.mfa]: 'Step-up Authentication (Conditional Access and MFA)',
  [onboardingFeatures.passwordManagement]: 'Password Management',
  [onboardingFeatures.radius]: 'Cloud RADIUS Authentication',
  [onboardingFeatures.remoteAssist]: 'Remote Assist',
  [onboardingFeatures.serverManagement]: 'Server Management & Authentication',
  [onboardingFeatures.ssoAuth]: 'SSO Authentication (SAML 2.0 and OIDC)',
  [onboardingFeatures.userManagement]: 'User Provisioning & Deprovisioning',
};

export const chipGroupFeatures = [
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.appleMdm],
    value: onboardingFeatures.appleMdm,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.compliance],
    value: onboardingFeatures.compliance,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.deviceManagement],
    value: onboardingFeatures.deviceManagement,
  },
  { name: onboardingFeatureDisplayNames[onboardingFeatures.ldap], value: onboardingFeatures.ldap },
  { name: onboardingFeatureDisplayNames[onboardingFeatures.mfa], value: onboardingFeatures.mfa },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.passwordManagement],
    value: onboardingFeatures.passwordManagement,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.remoteAssist],
    value: onboardingFeatures.remoteAssist,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.radius],
    value: onboardingFeatures.radius,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.serverManagement],
    value: onboardingFeatures.serverManagement,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.ssoAuth],
    value: onboardingFeatures.ssoAuth,
  },
  {
    name: onboardingFeatureDisplayNames[onboardingFeatures.userManagement],
    value: onboardingFeatures.userManagement,
  },
];

export const mspDefaultFeatures = [
  { value: onboardingFeatures.userManagement },
  { value: onboardingFeatures.deviceManagement },
  { value: onboardingFeatures.radius },
  { value: onboardingFeatures.ldap },
];

export const implementationChecklistTasks = {
  approveMdmProfile: 'approveMdmProfile',
  bindGroupToRadius: 'bindGroupToRadius',
  bindGroupToSamlApp: 'bindGroupToSamlApp',
  bindMdmEnrollmentPolicy: 'bindMdmEnrollmentPolicy',
  bindPolicyToDevice: 'bindPolicyToDevice',
  bindUserToDevice: 'bindUserToDevice',
  bindUserToUserGroup: 'bindUserToUserGroup',
  configureDeviceEnrollmentForMdm: 'configureDeviceEnrollmentForMdm',
  configureDeviceForRadius: 'configureDeviceForRadius',
  configureIdentityManagementForSaml: 'configureIdentityManagementForSaml',
  configureMdm: 'configureMdm',
  configureToUseLdap: 'configureToUseLdap',
  configureVpp: 'configureVpp',
  createConditionalAccessPolicy: 'createConditionalAccessPolicy',
  createDevice: 'createDevice',
  createExampleSamlApp: 'createExampleSamlApp',
  createMdmEnrollmentPolcy: 'createMdmEnrollmentPolcy',
  createPolicy: 'createPolicy',
  createRadius: 'createRadius',
  createServer: 'createServer',
  createUser: 'createUser',
  createUserGroup: 'createUserGroup',
  electLdapBindDnAccount: 'electLdapBindDnAccount',
  enableSSHPasswordLogin: 'enableSSHPasswordLogin',
  enrollUserViaUserGroups: 'enrollUserViaUserGroups',
  enterSessionCode: 'enterSessionCode',
  launchRemoteAssist: 'launchRemoteAssist',
  navigateToPasswordManager: 'navigateToPasswordManager',
  observeDirectoryInsights: 'observeDirectoryInsights',
  observeSystemInsights: 'observeSystemInsights',
  viewCloudInsights: 'viewCloudInsights',
};

export const implementationChecklistResources = {
  [onboardingFeatures.appleMdm]: {
    courseTitle: 'Adding Apple Devices Using MDM.',
    courseUrl: 'https://university.jumpcloud.com/courses/intro-to-mdm',
    hasVideo: false,
    image: 'img/growth/apple-mdm.png',
  },
  [onboardingFeatures.compliance]: {
    courseTitle: 'Intro to Directory Insights.',
    courseUrl: 'https://university.jumpcloud.com/courses/intro-to-directory-insights',
    hasVideo: false,
    image: 'img/growth/compliance.png',
  },
  [onboardingFeatures.deviceManagement]: {
    courseTitle: 'Adding Windows and Linux Devices.',
    courseUrl: 'https://university.jumpcloud.com/courses/adding-devices',
    hasVideo: false,
    image: 'img/growth/device-management.png',
  },
  [onboardingFeatures.ldap]: {
    courseTitle: 'Configuring LDAP.',
    courseUrl: 'https://university.jumpcloud.com/courses/configuring-ldap',
    hasVideo: false,
    image: 'img/growth/cloud-ldap.png',
  },
  [onboardingFeatures.mfa]: {
    courseTitle: 'Enabling MFA.',
    courseUrl: 'https://university.jumpcloud.com/courses/enabling-mfa',
    hasVideo: false,
    image: 'img/growth/step-up-authentication.png',
  },
  [onboardingFeatures.passwordManagement]: {
    courseTitle: 'Password Manager for Admins.',
    courseUrl: 'https://jumpcloud-1.wistia.com/medias/2zrx6tenq2',
    hasVideo: true,
    image: 'img/growth/password-manager.png',
  },
  [onboardingFeatures.radius]: {
    courseTitle: 'Configuring RADIUS.',
    courseUrl: 'https://university.jumpcloud.com/courses/configuring-radius',
    hasVideo: false,
    image: 'img/growth/cloud-radius.png',
  },
  [onboardingFeatures.remoteAssist]: {
    courseTitle: 'Remote Assist.',
    courseUrl: 'https://university.jumpcloud.com/courses/tutorial-remote-assist',
    hasVideo: true,
    image: 'img/growth/remote-assist.png',
  },
  [onboardingFeatures.ssoAuth]: {
    courseTitle: 'Configuring SSO.',
    courseUrl: 'https://university.jumpcloud.com/courses/configuring-sso',
    hasVideo: false,
    image: 'img/growth/sso-authentication.png',
  },
  [onboardingFeatures.userManagement]: {
    courseTitle: 'Provisioning & De-Provisioning Users.',
    courseUrl: 'https://university.jumpcloud.com/courses/provisioning-de-provisioning-users',
    hasVideo: false,
    image: 'img/growth/user-management.png',
  },
};

export const signupUseCases = {
  consolidatingITStack: 'Consolidating IT Stack',
  enablingHybrid: 'Enabling Hybrid / Remote Work',
  improvingSecurity: 'Improving Security',
  meetingCompliance: 'Meeting Compliance + Reporting Needs',
};

export const utmContentRemoteAssist = 'Remote-Assist';

export const keepGoingNotificationId = 'KeepGoingNotification';

export const valueReportStatTypes = {
  boolean: 'boolean',
  ratio: 'ratio',
  stat: 'stat',
  percent: 'percent',
};

export const valueReportDefinitions = {
  overviewSection: {
    applications: {
      label: 'Applications',
    },
    commands: {
      label: 'Commands',
    },
    devices: {
      label: 'Devices',
    },
    deviceGroups: {
      label: 'Device Groups',
    },
    users: {
      label: 'Users',
    },
    userGroups: {
      label: 'User Groups',
    },
    policies: {
      label: 'Policies',
    },
  },
  securityCompliance: {
    deviceEncrypted: {
      type: valueReportStatTypes.ratio,
      label: 'Devices Encrypted',
      definition: 'Total devices encrypted at the end of the reporting period (end of month)',
    },
    appleMdm: {
      type: valueReportStatTypes.ratio,
      label: 'Devices Enrolled in Apple MDM',
      definition: 'Total devices enrolled in apple MDM out of all macOS devices with the jumpCloud agent installed at the end of the reporting period (end of month)',
    },
    conditionalPolicies: {
      type: valueReportStatTypes.boolean,
      label: 'Conditional Access Policies',
      definition: 'Have any conditional access policies been configured? True/false - at the end of the reporting period (end of month)',
    },
    conditionalPoliciesUsers: {
      type: valueReportStatTypes.stat,
      label: 'Users Impacted by Conditional Access Policies',
      definition: 'Have any conditional access policies been configured? True/false - at the end of the reporting period (end of month)',
    },
    userMfa: {
      type: valueReportStatTypes.ratio,
      label: 'Users enrolled in MFA',
      definition: 'Total users currently enrolled in MFA at the end of the reporting period (end of month)',
    },
    deviceMfa: {
      type: valueReportStatTypes.stat,
      label: 'Devices Enrolled in MFA',
      definition: 'Total devices currently enrolled in MFA at the end of the reporting period (end of month)',
    },
    authenticationMfa: {
      type: valueReportStatTypes.percent,
      label: 'Authentications using MFA',
      definition: 'Percentage of authentications using MFA / all authentications - for the entire period (first day through last day of the previous calendar month)',
    },
    passwordComplexity: {
      type: valueReportStatTypes.boolean,
      label: 'Password Complexity Enforced',
      definition: 'Organization password settings configured to require at least one of: uppercase character, lowercase character, numeric character, and/or special character.',
    },
  },
  efficiency: {
    totalUserActivations: {
      type: valueReportStatTypes.stat,
      label: 'Total Users Activated',
      definition: 'Total users added to directory (activated/onboarded)  - for the entire period',
    },
    totalUserSuspensions: {
      type: valueReportStatTypes.stat,
      label: 'Total Users Suspended',
      definition: 'Total users deleted or suspended from directory - for the entire period',
    },
    scim: {
      type: valueReportStatTypes.stat,
      label: 'Identities Provisioned to Apps (SCIM)',
      definition: 'Of the identities provisioned through SCIM, how many of them were done automatically as a result of adding a user to a user group? - for the entire period',
    },
    recurringCommands: {
      type: valueReportStatTypes.stat,
      label: 'Scheduled/Recurring Commands',
      definition: 'Current total count of scheduled and recurring commands at runtime of report',
    },
  },
};

export const accountEmail = 'accounts@jumpcloud.com';
export const calendlyLink = 'https://calendly.com/get-started-with-jumpcloud/20-min';
export const freeSpecialistLink = 'https://hello.jumpcloud.com/calendar/team/t/58';

export const professionalServicesInfoLink = 'https://jumpcloud.com/professional-services';

export const upgradeModalProps = {
  upgradeContactSalesModal: 'Upgrade Contact Sales Modal',
  upgradeSalesWithCalendlyClicked: 'Contact Sales via Calendly Button Clicked',
};

export const trialModalLinks = {
  fairUsePolicy: {
    url: 'https://jumpcloud.com/fair-use-policy',
    text: 'fair use policy',
  },
  account: {
    url: '#/account/general',
    text: 'See More.',
  },
  pricing: {
    externalLink: 'https://jumpcloud.com/pricing',
    text: 'See More.',
  },
  salesContact: {
    url: `mailto:${accountEmail}`,
    text: 'Reach out to us',
  },
};

export const trialFeatures = {
  osPatchManagement: {
    featureCode: 'osPatchManagement',
  },

  zeroTrust: {
    featureCode: 'zeroTrust',
  },
};

export const trialPropsData = {
  zeroTrust: {
    featurePackageMarkdown: 'Platform*Plus*',
    eventLocation: eventLocations.conditionalPoliciesTrialModal,
    featureCode: 'zeroTrust',
    featureName: 'Conditional Access Policies',
    featurePackage: 'Platform Plus',
    recapDisclaimer: 'The number of Total Policies reflected above may be lower than expected if individual policies carry more than one condition. Policies with multiple conditions are counted as one policy.',
  },

  osPatchManagement: {
    eventLocation: eventLocations.patchManagementTrialModal,
    featureCode: 'osPatchManagement',
    featureName: 'Patch Management',
    featurePackage: 'Patch Management',
    isAddOn: true,
  },
};

export const capZeroStateViewCards = [
  {
    title: 'Device Trust',
    text: 'Create conditions to control how users or user groups can access applications from JumpCloud managed devices or unmanaged devices. For example, require MFA for users trying to access Slack from unmanaged devices.',
    iframeSrc: 'https://xd.adobe.com/embed/d9a5d73a-67ee-4d25-8956-b3aa1aa4b853-8cf6/',
    simulationUrl: 'https://jumpcloud.com/simulations/conditional-access-device-trust',
    guideName: 'devices',
  },

  {
    title: 'Network Trust',
    text: 'Create conditions to control how users and user groups can access cloud applications from IP addresses identified or not identified on your Conditional Lists. For example, deny access to users trying to access AWS SSO from IP addresses that are not on the Conditional List.',
    iframeSrc: 'https://xd.adobe.com/embed/50c9ca47-22da-40fb-a717-a4005738beca-059a/',
    simulationUrl: 'https://jumpcloud.com/simulations/conditional-access-network-trust',
    guideName: 'ipAddresses',
  },

  {
    title: 'Geolocation',
    text: 'Create conditions to control how users and user groups can access cloud applications from certain countries. For example, only allow users from the United States and Canada to access the User Portal.',
    iframeSrc: 'https://xd.adobe.com/embed/0f53e373-6b68-4fad-9358-a649dc8df477-5a00/',
    simulationUrl: 'https://jumpcloud.com/simulations/conditional-access-geolocation',
    guideName: 'locations',
  },
];

export const productTourLabels = {
  checklistLocation: 'Implementation Checklist',
  done: 'Done',
  hide: 'Hide',
  launch: 'Launch Product Tour',
  mainHeaderLocation: 'Main Header',
  onboardingLocation: 'Simplified Onboarding',
  popoverLocation: 'Product Tour Popover',
};

export default {
  chipGroupFeatures,
  defaultOnboardingFeatures,
  eventLocations,
  eventProperties,
  events,
  implementationChecklistTasks,
  implementationChecklistResources,
  itemTypePages,
  keepGoingNotificationId,
  landingPageReferralUrls,
  mspDefaultFeatures,
  onboardingFeatures,
  onboardingTasks,
  paymentModalNames,
  paymentSources,
  signupUseCases,
  termToFeatureMap,
  valueReportDefinitions,
  valueReportStatTypes,
};
